/**
 * API client.
 *
 * One place that knows how to talk to the server: how the access token is
 * attached, how errors are shaped, and what "offline" looks like. Feature code
 * calls `api.get('/invoices')` and deals with data or an ApiClientError — never
 * with fetch's habit of resolving successfully on a 500.
 */

const BASE = '/api';

/** Set by the auth store once a user signs in (Phase 1). */
let getAccessToken = () => null;
/** Called when the server says the token expired, so the session can refresh (Phase 1). */
let onUnauthorized = null;

export function configureApi({ accessTokenGetter, unauthorizedHandler }) {
  if (accessTokenGetter) getAccessToken = accessTokenGetter;
  if (unauthorizedHandler) onUnauthorized = unauthorizedHandler;
}

export class ApiClientError extends Error {
  constructor(message, { status, code, details, requestId } = {}) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details; // field-level errors, ready to show inline on a form
    this.requestId = requestId;
  }

  /** True when the request never reached the server — the phone lost signal. */
  get isOffline() {
    return this.code === 'network_error';
  }
}

async function request(method, path, { body, query, signal, headers = {}, raw = false, skipAuthRetry = false } = {}) {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const token = getAccessToken();
  const init = {
    method,
    signal,
    credentials: 'include', // the refresh token travels as an httpOnly cookie
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  };

  if (body !== undefined) {
    if (body instanceof FormData) {
      init.body = body; // let the browser set the multipart boundary
    } else {
      init.headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(body);
    }
  }

  let response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiClientError(
      'No connection to the server. Your work is saved on this device and will sync when you are back online.',
      { code: 'network_error' },
    );
  }

  // skipAuthRetry guards the refresh and login calls themselves: without it, a
  // 401 from /auth/refresh (no valid session cookie — the normal case on first
  // load) triggers this same retry logic, which calls doRefresh() again, which
  // is the very call still in flight — it ends up awaiting its own result and
  // never resolves. Any endpoint that participates in obtaining a session must
  // be excluded from triggering another attempt to obtain one.
  if (response.status === 401 && onUnauthorized && !skipAuthRetry) {
    const retried = await onUnauthorized(() =>
      request(method, path, { body, query, signal, headers, raw, skipAuthRetry }),
    );
    if (retried !== undefined) return retried;
  }

  if (raw) {
    if (!response.ok) throw await toError(response);
    return response;
  }

  if (response.status === 204) return null;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiClientError(payload?.error?.message ?? `Request failed (${response.status})`, {
      status: response.status,
      code: payload?.error?.code,
      details: payload?.error?.details,
      requestId: payload?.error?.requestId ?? response.headers.get('X-Request-Id'),
    });
  }

  return payload;
}

async function toError(response) {
  const payload = await response.json().catch(() => null);
  return new ApiClientError(payload?.error?.message ?? `Request failed (${response.status})`, {
    status: response.status,
    code: payload?.error?.code,
    details: payload?.error?.details,
    requestId: response.headers.get('X-Request-Id'),
  });
}

export const api = {
  get: (path, options) => request('GET', path, options),
  post: (path, body, options) => request('POST', path, { ...options, body }),
  patch: (path, body, options) => request('PATCH', path, { ...options, body }),
  put: (path, body, options) => request('PUT', path, { ...options, body }),
  delete: (path, options) => request('DELETE', path, options),
  /** For PDF downloads — returns the Response so the caller can read a blob. */
  raw: (path, options) => request('GET', path, { ...options, raw: true }),
};

/** Fetches an authenticated file (CSV, PDF, …) and triggers a browser save — the same blob-URL dance every export button in the app needs. */
export async function downloadFile(path, filename, options) {
  const res = await api.raw(path, options);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export default api;
